import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format, formatStringForAppTypeLower } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, CredentialTypeOptions, office365AuthTypes } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';

const servicePrincipalKeyControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'password',
        label: stringResources.ModelName_ServicePrincipalKey,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Office365_DisplayText),
        stringResources.ModelName_ServicePrincipalKey
    )
};

const spSelector: ControlMetadata = {
    type: 'radio-switch',
    bind: 'servicePrincipalCredentialType',
    label: stringResources.ModelName_ServicePrincipalCredentialType,
    isLabelVertical: true,
    nested: 1,
    options: [
        {
            label: stringResources.ModelName_ServicePrincipalCert,
            value: servicePrincipalCredentialTypes.ServicePrincipalCert
        },
        {
            label: stringResources.ModelName_ServicePrincipalKey,
            value: servicePrincipalCredentialTypes.ServicePrincipalKey
        }
    ],
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    defaultValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
    cases: [
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
            children: [servicePrincipalKeyControl]
        },
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
            children: [
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'servicePrincipalEmbeddedCert',
                    label: stringResources.ModelName_ServicePrincipalCert,
                    child: {
                        type: 'key-file-content',
                        label: stringResources.ModelName_ServicePrincipalCert,
                        required: true,
                        isLabelVertical: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    },
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Office365_DisplayText),
                        stringResources.ModelName_ServicePrincipalCert
                    )
                },
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'servicePrincipalEmbeddedCertPassword',
                    label: stringResources.ModelName_ServicePrincipalCertPassword,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_ServicePrincipalCertPassword,
                        isLabelVertical: true
                    },
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Office365_DisplayText),
                        stringResources.ModelName_ServicePrincipalCertPassword
                    )
                }
            ]
        }
    ]
};

const servicePrincipalControl: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    { ...spSelector }
];

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: office365AuthTypes.ServicePrincipal
        },
        {
            label: stringResources.ModelName_UserAuthentication,
            value: office365AuthTypes.UserAuth
        }
    ],
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    defaultValue: office365AuthTypes.ServicePrincipal,
    cases: [
        {
            caseValue: office365AuthTypes.ServicePrincipal,
            children: servicePrincipalControl
        },
        {
            caseValue: office365AuthTypes.UserAuth,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};

const commonControls: ControlMetadata[] = [
    {
        type: 'tenant-id-select',
        bind: 'office365TenantId',
        label: stringResources.ModelName_Office365Tenant,
        description: formatStringForAppTypeLower(stringResources.Office365Tenant_tooltip),
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        defaultToCurrentTenant: true,
        disabled: true,
        isLabelVertical: true
    }
];

const dataflowMicrosoft365Source: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.Microsoft365,
    settings: adfModelTypeMap.office365.dataflowSettings,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.Microsoft365_DisplayText),
    controls: [
        {
            type: 'dataflow-microsoft365',
            bind: {
                tableName: 'tableName',
                autoFlatten: 'autoFlatten',
                userScope: 'userScope',
                userScopeFilterUri: 'userScopeFilterUri',
                allowedGroups: 'allowedGroups',
                dateFilterColumn: 'dateFilterColumn',
                startTime: 'startTime',
                endTime: 'endTime',
                structure: 'structure'
            }
        }
    ]
};

export const office365Metadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(injector, 'ExposureControlService');
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.office365.linkedServiceType,
        displayName: __IS_FABRIC_DPE__
            ? stringResources.Microsoft365_DisplayText
            : stringResources.Office365_DisplayText,
        linkedServiceNamePrefix: 'Microsoft365',
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-o365'
        },
        onlyAzureIR: true,
        isFirstClass: true,
        supportDatasetV2: true,
        supportedInTrident: true,
        supportCopyJobSource: GlobalVariables.copyJobConnectorsPhase2,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        notShowInTypeSelection:
            __IS_FABRIC_DPE__ &&
            (GlobalVariables.isMicrosoftInternal || GlobalVariables.fabricEnv === 'DXT') &&
            !GlobalVariables.enableM365,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'Microsoft365' as ResourceKind
        },
        // supportOPDG: exposureControlService.enableOPDG,
        supportedAuthKindsInTrident: [ConnectionAuthKind.ServicePrincipal],
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportDataFlowSource: () => exposureControlService.enableMicrosoft365DataFlow,
        dataflowSource: [dataflowMicrosoft365Source],
        timedQueryTemplate: '',
        onetimeQueryTemplate: '',
        category: ['ServicesApps'],
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.office365.copySourceType],
        controls: GlobalVariables.enableConnectorUserAuthCred
            ? commonControls.concat([authenticationTypeControl])
            : commonControls.concat(servicePrincipalControl)
    };

    return metadata;
};
